import {
  typedCollection,
  typedDoc,
  getCollection,
  getDocument,
  addDoc,
  updateDocument,
  orderBy,
  where,
  serverTimestamp,
} from '@/lib/firebase/firestore'
import type { BoardTemplateDocument, SeedBoard } from '@/types'
import { saveBoardAsTemplateFn, reconcileOverviewBoardFn } from '@/lib/firebase/functions'

const templatesCol = () => typedCollection<BoardTemplateDocument>('boardTemplates')

const BLANK_ROOT: SeedBoard = {
  columns: [
    { id: 'backlog', label: 'Backlog', order: 0 },
    { id: 'in_progress', label: 'In Progress', order: 1 },
    { id: 'review', label: 'Review', order: 2 },
    { id: 'done', label: 'Done', order: 3 },
  ],
  tasks: [],
}

export const boardTemplatesService = {
  list(): Promise<Array<BoardTemplateDocument & { id: string }>> {
    return getCollection(
      templatesCol(),
      where('isArchived', '==', false),
      orderBy('name', 'asc')
    )
  },

  get(id: string): Promise<BoardTemplateDocument | null> {
    return getDocument(typedDoc<BoardTemplateDocument>('boardTemplates', id))
  },

  async createBlank(input: {
    name: string
    description?: string
    defaultForServiceIds: string[]
    createdBy: string
  }): Promise<string> {
    const ref = await addDoc(templatesCol(), {
      name: input.name,
      ...(input.description ? { description: input.description } : {}),
      root: BLANK_ROOT,
      defaultForServiceIds: input.defaultForServiceIds,
      isArchived: false,
      createdBy: input.createdBy,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    } as unknown as BoardTemplateDocument)
    return ref.id
  },

  async update(
    id: string,
    patch: Partial<
      Pick<BoardTemplateDocument, 'name' | 'description' | 'defaultForServiceIds' | 'isArchived'>
    >
  ): Promise<void> {
    await updateDocument(typedDoc<BoardTemplateDocument>('boardTemplates', id), {
      ...patch,
      updatedAt: serverTimestamp(),
    } as unknown as BoardTemplateDocument)
  },

  async archive(id: string): Promise<void> {
    await this.update(id, { isArchived: true })
  },

  async saveBoardAsTemplate(input: {
    boardId: string
    name: string
    description?: string
    defaultForServiceIds?: string[]
  }): Promise<string> {
    const { data } = await saveBoardAsTemplateFn(input)
    return data.templateId
  },

  async reconcileOverview(
    boardId: string,
    templateOverrides?: Record<string, string>
  ): Promise<{ added: number; addedServiceIds: string[] }> {
    const { data } = await reconcileOverviewBoardFn({
      boardId,
      ...(templateOverrides ? { templateOverrides } : {}),
    })
    return data
  },
}
